import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { AdminHealthStatusCard } from "@/pages/admin/AdminHealthStatusCard";

interface HubCard {
  href: string;
  title: string;
  description: string;
  icon: ReactNode;
  badge?: string;
  /** Innerhalb der React-SPA – Routerlink ohne Reload. */
  internal?: boolean;
}

const HUB_CARDS: HubCard[] = [
  {
    href: "/admin/einstellungen",
    internal: true,
    title: "Einstellungen",
    description: "Ordner, Kunden, Orte, Ansprechpartner und weitere Stammdaten pflegen.",
    icon: <CogIcon />,
    badge: "React",
  },
  {
    href: "/admin/admin-area",
    internal: true,
    title: "Admin-Bereich",
    description: "Neuigkeiten veröffentlichen, Projekte importieren und weitere Admin-Werkzeuge.",
    icon: <ShieldIcon />,
    badge: "React",
  },
  {
    href: "/admin/field-templates",
    internal: true,
    title: "Feld-Templates",
    description: "Vorlagen für Stammdaten- und Wartungsformular-Felder verwalten.",
    icon: <PuzzleIcon />,
    badge: "React",
  },
  {
    href: "/offpaper",
    internal: true,
    title: "Offpaper Formulare",
    description: "Lokale Offpaper-Einträge einsehen, exportieren und aus der Cloud nachladen.",
    icon: <DocumentIcon />,
    badge: "React",
  },
];

export function AdminIndexPage() {
  return (
    <AppShell
      title="Administration"
      subtitle="Zentraler Einstieg in alle administrativen Bereiche der Ernsthaus App."
      backExternal={{ href: "/", label: "zur klassischen Startseite" }}
    >
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <AdminHealthStatusCard />
        {HUB_CARDS.map((card) => {
          const cardClass =
            "group flex h-full flex-col gap-3 rounded-ios border border-separator bg-card p-5 shadow-ios transition hover:-translate-y-0.5 hover:border-accent hover:shadow-ios-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent";
          const inner = (
            <>
              <div className="flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-soft text-accent transition group-hover:bg-fill-secondary">
                  {card.icon}
                </span>
                {card.badge && (
                  <span
                    className={
                      card.badge === "React"
                        ? "rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-accent"
                        : "rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-label-secondary"
                    }
                  >
                    {card.badge}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-base font-semibold text-label">{card.title}</h2>
                <p className="mt-1 text-sm text-label-secondary">{card.description}</p>
              </div>
              <span
                aria-hidden
                className="ml-auto mt-auto text-lg text-label-tertiary transition group-hover:translate-x-0.5 group-hover:text-accent"
              >
                ›
              </span>
            </>
          );
          if (card.internal) {
            return (
              <Link key={card.href} to={card.href} className={cardClass}>
                {inner}
              </Link>
            );
          }
          return (
            <a key={card.href} href={card.href} className={cardClass}>
              {inner}
            </a>
          );
        })}
      </section>
    </AppShell>
  );
}

function CogIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PuzzleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M10 4h4v3a2 2 0 1 0 4 0v0h2v4h-3a2 2 0 1 0 0 4h3v4h-4v0a2 2 0 1 0-4 0v0H6v-4h0a2 2 0 1 0 0-4H6V7h4v0a2 2 0 1 0 0-3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M7 3.5h7l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-9.5A1.5 1.5 0 0 1 5.5 20V5A1.5 1.5 0 0 1 7 3.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 3.5V8h4.5M9 12h6M9 15.5h6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
